<template>
    <div class="Datasets">
        <Transition
            name="fade"
            mode="out-in"
        >
            <q-card
                class="DatasetsCard"
                bordered
            >
                <q-card-section class="DatasetsBody">
                    <DatasetsListCards
                        ref="table"
                        :rows="datasets"
                        :total="total"
                        :search="(filters || {}).search"
                        :archived="isProjectArchived"
                        :loading="isFetching"
                        :pagination="pagination"
                        @on-new-item="handleCreateItem"
                        @on-delete="handleDeleteItem"
                        @on-update="handleEditItem"
                        @on-upload="handleUploadItem"
                        @on-replace="handleReplaceItem"
                        @on-fetch-data="fetchData"
                        @updatePagination="updatePagination"
                        @on-visualization="handleGoToVisualization"
                        @on-settings="handleOpenDatasetInfo"
                        @on-move="handleOpenMoveDatasetModal"
                    />
                </q-card-section>
            </q-card>
        </Transition>
        <MoveDatasetModal
            v-if="openMoveModal"
            :dataset="selectedDataset"
            :currentProjectId="projectId"
            :isOpen="openMoveModal"
            @close="openMoveModal = false"
            @moved="handleDatasetMoved"
        />
        <ModalRequest
            ref="deleteDialog"
            :title="$t('projects.datasets.delete')"
            :text="$t('projects.datasets.modals.delete.text', { name: (selected || {}).name })"
            :loading="isLoading"
            @on-accept="archiveDataset"
        />

        <ModalNameDesc
            ref="newDialog"
            :title="$t('projects.datasets.new')"
            :action="$t('actions.create')"
            :loading="isLoading"
            @on-submit="createDataset"
        />

        <ModalNameDesc
            ref="updateDialog"
            :title="$t('projects.datasets.edit')"
            :action="$t('actions.save')"
            :loading="isLoading"
            @on-submit="updateDataset"
        />

        <UploadDialog
            ref="uploadDialog"
            @finish-update="fetchData"
        />
        <UploadDialog
            ref="replaceDialog"
            type="replace"
            @finish-update="fetchData"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { pollingMixin } from '@/mixins'
import { ModalRequest, ModalNameDesc } from '@/components'
import { WIZARD } from '@/entities'
import {
    DatasetsListCards, UploadDialog, MoveDatasetModal,
} from './sections'

export default {
    name: 'DatasetsView',
    components: {
        ModalRequest,
        DatasetsListCards,
        ModalNameDesc,
        UploadDialog,
        MoveDatasetModal,
    },
    mixins: [pollingMixin],
    props: {
        projectId: VueTypes.string,
        isProjectArchived: VueTypes.bool,
        sortBy: VueTypes.string,
        sortDirection: VueTypes.string,
        filters: VueTypes.shape({
            search: VueTypes.string,
            archived: VueTypes.oneOfType([VueTypes.string, VueTypes.bool]),
        }),
    },
    data() {
        return {
            firstTime: true,
            datasets: undefined,
            total: 0,
            isFetching: false,
            selected: undefined,
            isLoading: false,
            openMoveModal: false,
            pagination: {},
        }
    },
    computed: {
        params() {
            return {
                ...(this.filters || {}),
                ...this.pagination,
            }
        },
    },
    watch: {
        filters: {
            handler(value) {
                if (value) this.fetchData()
            },
        },
    },

    mounted() {
        const storedPaginations = localStorage.getItem('datasetsPagination')
        const paginationMap = storedPaginations ? JSON.parse(storedPaginations) : {}
        if (paginationMap[this.projectId]) {
            this.pagination = paginationMap[this.projectId]
        } else {
            const { table } = this.$refs
            this.pagination = table ? table.getPagination() : {}

            paginationMap[this.projectId] = this.pagination
            localStorage.setItem('datasetsPagination', JSON.stringify(paginationMap))
        }
        this.fetchData()
    },
    methods: {
        handleCreateItem() {
            this.$storage.remove(WIZARD.KEYS.DATASET)
            this.$router.push({ name: 'WizardDataset', params: { projectId: this.projectId } })
        },
        handleDeleteItem(row) {
            this.selected = row
            this.$refs.deleteDialog.open()
        },
        handleEditItem(row) {
            this.selected = row
            this.$refs.updateDialog.open({
                uuid: row?.uuid,
                name: row?.name,
                description: row?.description,
            })
        },
        handleUploadItem({ uuid }) {
            this.$refs.uploadDialog.open(uuid)
        },
        handleReplaceItem({ uuid }) {
            this.$refs.replaceDialog.open(uuid)
        },
        handleOpenDatasetInfo(row) {
            this.$emit('open-dataset-info', row)
        },
        handleOpenMoveDatasetModal(dataset) {
            this.selectedDataset = dataset
            this.openMoveModal = true
        },
        handleCloseMoveDataset() {
            this.openMoveModal = false
            this.selectedDataset = null
        },
        handleDatasetMoved() {
            this.openMoveModal = false
            this.fetchData()
        },
        handleGoToDataset({ uuid: datasetId }) {
            this.$router.push({ name: 'Dataset', params: { projectId: this.projectId, datasetId } })
        },
        handleGoToVisualization({ uuid: processId, name, opened }) {
            const { projectId } = this
            if (!opened) {
                this.checkInitialView(projectId, processId, name)
            } else {
                sessionStorage.removeItem('sessionCharts')
                this.$router.push({ name: 'ProcessVariants', params: { projectId, processId } })
            }
        },
        updatePagination(props) {
            this.pagination.page = props.page
            this.pagination.rowsPerPage = props.rowsPerPage
            this.savePagination()
            this.fetchData()
        },
        savePagination() {
            const storedPaginations = localStorage.getItem('datasetsPagination')
            const paginationMap = storedPaginations ? JSON.parse(storedPaginations) : {}

            paginationMap[this.projectId] = this.pagination
            localStorage.setItem('datasetsPagination', JSON.stringify(paginationMap))
        },
        async fetchData({ polling = false } = {}) {
            const {
                archived, search, page, rowsPerPage,
            } = this.params
            const params = {
                projectUid: this.projectId,
                includeArchived: !!archived,
                ...(rowsPerPage ? { start: (page - 1) * rowsPerPage } : {}),
                ...(rowsPerPage ? { limit: rowsPerPage } : {}),
                sortBy: this.sortBy || 'CREATED_AT',
                sortDirection: this.sortDirection || 'DESCENDING',
                search: search || null,
                polling,
            }

            if (!(this.datasets || []).length) this.isFetching = true
            apiRequest(Api().datasets.list(params))
                .then(({ data, total }) => {
                    this.datasets = data
                    this.total = total
                    console.log('Fetched datasets', { data, total })
                })
                .catch(notifyError)
                .finally(() => { this.isFetching = false })
        },
        async createDataset({ name, description }) {
            this.isLoading = true
            apiRequest(Api().datasets.create({ projectUid: this.projectId, name, description }))
                .then(() => {
                    notifySuccess(this.$t('projects.datasets.notifications.created.text'))
                    this.fetchData()
                    this.$refs.newDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async updateDataset(datasetInfo) {
            const datasetId = datasetInfo?.uuid || this.selected?.uuid
            if (!datasetId) return

            this.isLoading = true
            apiRequest(Api().datasets.update({ datasetId, params: { name: datasetInfo.name, description: datasetInfo.description } }))
                .then(() => {
                    notifySuccess(this.$t('projects.datasets.notifications.updated.text'))
                    this.fetchData()

                    if (this.selected?.uuid === datasetId) {
                        this.selected.name = datasetInfo.name
                        this.selected.description = datasetInfo.description
                    }

                    this.$refs.updateDialog?.close?.()
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                })
        },
        async archiveDataset() {
            const { uuid: datasetId } = this.selected
            this.isLoading = true
            apiRequest(Api().datasets.delete({ datasetId }))
                .then(() => {
                    notifySuccess(this.$t('projects.datasets.notifications.deleted.text'))
                    this.fetchData()
                    this.$refs.deleteDialog.close()
                    this.selected = undefined
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async checkInitialView(projectId, processId, name) {
            this.isLoading = true

            sessionStorage.removeItem('sessionCharts')
            apiRequest(Api().datasets.setOpened({ datasetId: processId }))
                .then(() => {
                    this.$router.push({
                        name: 'ProcessVariants',
                        params: { projectId, processId },
                        query: { datasetName: name },
                    })
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
    },
}
</script>
